import React, { useState, useEffect } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Info, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';

const FeeConfigurationPanel = ({ onConfigChange, initialConfig }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  // Don't set hardcoded defaults - load from database
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Sample calculation for preview (using ₹10,000 as example)
  const SAMPLE_AMOUNT = 10000;

  useEffect(() => {
    // Only load fee config once when component mounts
    // Don't reset when initialConfig changes to prevent circular updates
    console.log('🔵 [FeeConfigurationPanel] Component mounted, loading fee config');
    loadFeeConfig();
  }, []); // Empty dependency array - only run once on mount

  const loadFeeConfig = async () => {
    console.log('🔵 [FeeConfigurationPanel] loadFeeConfig called');
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getFeeConfig();
      console.log('🔵 [FeeConfigurationPanel] getFeeConfig response:', response.data);
      if (response.data.success && response.data.data) {
        const loadedConfig = response.data.data;
        console.log('🔵 [FeeConfigurationPanel] Setting config to:', loadedConfig);
        setConfig(loadedConfig);
        if (onConfigChange) {
          console.log('🔵 [FeeConfigurationPanel] Calling onConfigChange on load with:', loadedConfig);
          onConfigChange(loadedConfig);
        }
      } else {
        console.error('❌ [FeeConfigurationPanel] Invalid response format:', response.data);
        setError('Invalid fee configuration response from server');
      }
    } catch (err) {
      console.error('❌ [FeeConfigurationPanel] Error loading config:', err);
      let errorMessage = 'Failed to load fee configuration';
      let statusCode = null;
      
      if (err.response) {
        // API responded with error status
        statusCode = err.response.status;
        errorMessage = err.response.data?.error || err.response.data?.details || errorMessage;
        if (statusCode === 503) {
          errorMessage = 'Service unavailable. Please try again later.';
        } else if (statusCode === 404) {
          errorMessage = 'Fee configuration endpoint not found.';
        } else if (statusCode === 401 || statusCode === 403) {
          errorMessage = 'Not authorized to load fee configuration.';
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else went wrong
        errorMessage = err.message || errorMessage;
      }
      
      setError(`${errorMessage}${statusCode ? ` (HTTP ${statusCode})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    console.log('🔵 [FeeConfigurationPanel] Input changed - field:', field, 'value:', value);
    // Convert empty string to 0, otherwise parse to float
    const numValue = value === '' ? 0 : parseFloat(value);
    const updatedConfig = { ...config, [field]: numValue };
    console.log('🔵 [FeeConfigurationPanel] Updated config:', updatedConfig);
    setConfig(updatedConfig);
    // DO NOT call onConfigChange here - only call it when saving!
    // This prevents the circular update loop that resets the values
    setSuccess(null); // Clear success message when editing
  };

  const validateConfig = () => {
    const errors = [];
    if (!config) {
      errors.push('Configuration not loaded');
      return errors;
    }

    const pgFee = config.pg_fee_percentage ?? 0;
    const gstFee = config.gst_percentage ?? 0;
    const ttFee = config.theraptrack_fee_percentage ?? 0;
    const mktFee = config.marketplace_fee_percentage ?? 0;

    if (pgFee < 0 || pgFee > 100) {
      errors.push('Payment Gateway fee must be between 0-100%');
    }
    if (gstFee < 0 || gstFee > 100) {
      errors.push('GST must be between 0-100%');
    }
    if (ttFee < 0 || ttFee > 100) {
      errors.push('TheraP Track fee must be between 0-100%');
    }
    if (mktFee < 0 || mktFee > 100) {
      errors.push('Marketplace fee must be between 0-100%');
    }

    const totalFeePercent = calculateTotalFeePercent();
    if (totalFeePercent > 50) {
      errors.push(`Warning: Total fees are ${totalFeePercent.toFixed(2)}% of gross amount (>50%)`);
    }

    return errors;
  };

  const handleSave = async () => {
    console.log('🔵 [FeeConfigurationPanel] handleSave called');
    console.log('🔵 [FeeConfigurationPanel] Current config state:', config);

    const errors = validateConfig();
    if (errors.length > 0) {
      console.log('❌ [FeeConfigurationPanel] Validation errors:', errors);
      setError(errors.join(', '));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      console.log('🔵 [FeeConfigurationPanel] Calling updateFeeConfig with:', config);
      const response = await adminAPI.updateFeeConfig(config);
      console.log('✅ [FeeConfigurationPanel] Response received:', response.data);

      if (response.data.success) {
        const savedConfig = response.data.data;
        console.log('✅ [FeeConfigurationPanel] Saved config:', savedConfig);
        setConfig(savedConfig); // Update local state with saved values
        setSuccess('Fee configuration saved successfully');
        if (onConfigChange) {
          console.log('🔵 [FeeConfigurationPanel] Calling onConfigChange with:', savedConfig);
          onConfigChange(savedConfig);
        }
      }
    } catch (err) {
      console.error('❌ [FeeConfigurationPanel] Error saving:', err);
      console.error('❌ [FeeConfigurationPanel] Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to save fee configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadFeeConfig();
    setSuccess(null);
    setError(null);
  };

  const calculatePreview = () => {
    if (!config) {
      return {
        grossAmount: SAMPLE_AMOUNT,
        pgFee: 0,
        gstAmount: 0,
        theraptrackFee: 0,
        totalFee: 0,
        netAmount: SAMPLE_AMOUNT
      };
    }

    const grossAmount = SAMPLE_AMOUNT;
    const pgFee = (grossAmount * (config.pg_fee_percentage || 0)) / 100;
    const gstAmount = (pgFee * (config.gst_percentage || 0)) / 100;
    const theraptrackFee = (grossAmount * (config.theraptrack_fee_percentage || 0)) / 100;
    const marketplaceFee = (grossAmount * (config.marketplace_fee_percentage || 0)) / 100;
    const totalFee = pgFee + gstAmount + theraptrackFee;
    const totalFeeMarketplace = pgFee + gstAmount + marketplaceFee;
    const netAmount = grossAmount - totalFee;
    const netAmountMarketplace = grossAmount - totalFeeMarketplace;

    return {
      grossAmount,
      pgFee,
      gstAmount,
      theraptrackFee,
      marketplaceFee,
      totalFee,
      totalFeeMarketplace,
      netAmount,
      netAmountMarketplace
    };
  };

  const calculateTotalFeePercent = () => {
    if (!config) return 0;
    const preview = calculatePreview();
    return (preview.totalFee / preview.grossAmount) * 100;
  };

  const preview = calculatePreview();

  return (
    <div className="card mb-6">
      <div
        className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center gap-2">
              Fee Configuration
              <Info className="h-4 w-4 text-gray-400" />
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Configure default fee percentages for all payouts
            </p>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-4" onClick={(e) => e.stopPropagation()}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 mb-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">Error loading fee configuration</span>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
                  <button
                    onClick={loadFeeConfig}
                    disabled={loading}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Retrying...' : 'Retry'}
                  </button>
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400">
                  <span className="text-sm">{success}</span>
                </div>
              )}

              {!config ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Payment Gateway Fee */}
                    <div className="space-y-2">
                      <label htmlFor="pg_fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        Payment Gateway Fee (%)
                        <span className="text-xs text-gray-500">(on gross amount)</span>
                      </label>
                      <input
                        id="pg_fee"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={config.pg_fee_percentage ?? 0}
                        onChange={(e) => handleInputChange('pg_fee_percentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                      />
                    </div>

                    {/* GST */}
                    <div className="space-y-2">
                      <label htmlFor="gst" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        GST (%)
                        <span className="text-xs text-gray-500">(on PG fee)</span>
                      </label>
                      <input
                        id="gst"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={config.gst_percentage ?? 0}
                        onChange={(e) => handleInputChange('gst_percentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                      />
                    </div>

                    {/* TheraP Track Fee (Direct Link) */}
                    <div className="space-y-2">
                      <label htmlFor="theraptrack_fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        Direct Link Fee (%)
                        <span className="text-xs text-gray-500">(on gross amount)</span>
                      </label>
                      <input
                        id="theraptrack_fee"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={config.theraptrack_fee_percentage ?? 0}
                        onChange={(e) => handleInputChange('theraptrack_fee_percentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                      />
                    </div>

                    {/* Marketplace Fee */}
                    <div className="space-y-2">
                      <label htmlFor="marketplace_fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        Marketplace Fee (%)
                        <span className="text-xs text-gray-500">(on gross amount)</span>
                      </label>
                      <input
                        id="marketplace_fee"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={config.marketplace_fee_percentage ?? 0}
                        onChange={(e) => handleInputChange('marketplace_fee_percentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                      />
                    </div>
                  </div>

                  {/* Preview Calculation */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-blue-900">
                      Preview: Sample Calculation for ₹{SAMPLE_AMOUNT.toLocaleString('en-IN')}
                    </h4>

                    {/* Direct Link Scenario */}
                    <div>
                      <p className="text-xs font-semibold text-blue-800 mb-1">Direct Link Booking</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Gross Amount:</span>
                          <span className="ml-2 font-medium">₹{preview.grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">PG Fee:</span>
                          <span className="ml-2 font-medium">₹{preview.pgFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">GST:</span>
                          <span className="ml-2 font-medium">₹{preview.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Platform Fee:</span>
                          <span className="ml-2 font-medium">₹{preview.theraptrackFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Fees:</span>
                          <span className="ml-2 font-medium text-red-600">₹{preview.totalFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Net Amount:</span>
                          <span className="ml-2 font-medium text-green-600">₹{preview.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Marketplace Scenario */}
                    <div className="border-t border-blue-200 pt-2">
                      <p className="text-xs font-semibold text-blue-800 mb-1">Marketplace Booking</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Gross Amount:</span>
                          <span className="ml-2 font-medium">₹{preview.grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">PG Fee:</span>
                          <span className="ml-2 font-medium">₹{preview.pgFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">GST:</span>
                          <span className="ml-2 font-medium">₹{preview.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Platform Fee:</span>
                          <span className="ml-2 font-medium">₹{preview.marketplaceFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Fees:</span>
                          <span className="ml-2 font-medium text-red-600">₹{preview.totalFeeMarketplace.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Net Amount:</span>
                          <span className="ml-2 font-medium text-green-600">₹{preview.netAmountMarketplace.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Save as Default
                    </button>
                    <button
                      onClick={handleReset}
                      disabled={loading || saving}
                      className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reset to Default
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FeeConfigurationPanel;
